import { Injectable } from '@angular/core';

import { Action, Selector, State, StateContext } from '@ngxs/store';
import { append, patch, removeItem, updateItem } from '@ngxs/store/operators';
import { chunk } from 'lodash';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ListProduct, IPagination, BrandMenuItem, ListFavorites, PaginationFilters } from 'src/app/interfaces';
import { ProductsService } from 'src/app/services';
import {
    AddFavoriteBrand,
    BrandsSearch,
    GetBrands,
    GetFavorites,
    GetProductsSearch,
    GetStandardFilters,
    RemoveFavoriteBrand,
    ResetSearchPage,
    SearchProducts,
    SetListFilters,
    UpdateAppFavorites
} from './products.actions';
interface ProductsModel {
    searchProducts: ListProduct[];
    searchPagination: IPagination;
    favoritesPagination: IPagination;
    productsListTitle: string;
    brands: BrandMenuItem[];
    favorites: ListFavorites[];
    brandsSearch: string;
    searchFilters: PaginationFilters;
    standardFilters: PaginationFilters;
    favBrands: string[];
}

@State<ProductsModel>({
    name: 'products',
    defaults: {
        searchProducts: [],
        searchPagination: {
            limit: 30,
            page: 1,
            search: '',
            filter: null
        },
        favoritesPagination: {
            limit: 1000,
            page: 1,
            search: '',
            filter: null
        },
        productsListTitle: '',
        brands: [],
        favorites: [],
        brandsSearch: '',
        searchFilters: null,
        standardFilters: null,
        favBrands: []
    }
})
@Injectable()
export class ProductsState {
    constructor(private service: ProductsService) {}

    @Selector()
    static favBrands(state: ProductsModel) {
        return state.favBrands;
    }

    @Selector()
    static searchFilters(state: ProductsModel) {
        return state.searchFilters;
    }

    @Selector()
    static brands(state: ProductsModel) {
        return state.brands;
    }

    @Selector()
    static brandsSearch(state: ProductsModel) {
        return state.brandsSearch;
    }

    @Selector()
    static listTitle(state: ProductsModel) {
        return state.productsListTitle;
    }

    @Selector()
    static searchProducts(state: ProductsModel) {
        return chunk(state.searchProducts, 2);
    }

    @Selector()
    static totalSearchProducts(state: ProductsModel) {
        return state.searchPagination.total;
    }

    @Selector()
    static currentTotalSearchProducts(state: ProductsModel) {
        return state.searchProducts.length;
    }

    @Selector()
    static searchQuery(state: ProductsModel) {
        return state.searchPagination.search;
    }

    @Selector()
    static searchPagination(state: ProductsModel) {
        return state.searchPagination;
    }
    @Selector()
    static favorites(state: ProductsModel) {
        return state.favorites;
    }
    @Selector()
    static favoritesCount(state: ProductsModel) {
        return state.favorites?.length;
    }

    @Action(GetProductsSearch, { cancelUncompleted: true })
    getSearchAll(ctx: StateContext<ProductsModel>) {
        return this.service.getAll(ctx.getState().searchPagination).pipe(
            tap((response) => {
                ctx.setState(
                    patch({
                        searchProducts: append(response.docs)
                    })
                );
                ctx.patchState({
                    searchPagination: {
                        limit: response.limit,
                        total: response.total,
                        page: response.page + 1,
                        filter: response.filter,
                        search: response.search
                    },
                    searchFilters: response.availableFilters
                });
            })
        );
    }

    @Action(GetFavorites, { cancelUncompleted: true })
    getFavorites(ctx: StateContext<ProductsModel>) {
        return this.service.getMyFavorites(ctx.getState().favoritesPagination).pipe(
            catchError((err) => of({ docs: [] })),
            tap((response) => {
                ctx.setState(
                    patch({
                        favorites: response.docs
                    })
                );
            })
        );
    }

    @Action(UpdateAppFavorites)
    updateAppFavs(ctx: StateContext<ProductsModel>, { payload }: UpdateAppFavorites) {
        ctx.dispatch(new GetFavorites());
        ctx.setState(
            patch({
                searchProducts: updateItem<ListProduct>(
                    (product) => product.id === payload.id,
                    patch<ListProduct>({ isFavorite: payload.isFavorite, likes: payload.likes })
                )
            })
        );
    }

    @Action(ResetSearchPage, { cancelUncompleted: true })
    resetSearchAll(ctx: StateContext<ProductsModel>) {
        ctx.patchState({
            searchPagination: {
                limit: 30,
                page: 1,
                search: '',
                filter: null
            },
            searchProducts: [],
            searchFilters: ctx.getState().standardFilters
        });
    }

    @Action(SearchProducts)
    search(ctx: StateContext<ProductsModel>, { payload, filters }: SearchProducts) {
        ctx.patchState({
            searchPagination: {
                ...{
                    limit: 30,
                    page: 1,
                    search: '',
                    filter: null,
                    total: 0
                },
                ...{ search: payload },
                ...{ filter: filters }
            }
        });

        return this.service.getAll(ctx.getState().searchPagination).pipe(
            tap((response) => {
                ctx.patchState({
                    searchProducts: response.docs,
                    searchPagination: {
                        limit: response.limit,
                        page: response.page + 1,
                        search: payload,
                        filter: response.filter,
                        total: response.total
                    },
                    searchFilters: response.availableFilters
                });
            })
        );
    }

    @Action(SetListFilters)
    setListFilters(ctx: StateContext<ProductsModel>, { payload, title }: SetListFilters) {
        ctx.patchState({
            searchProducts: [],
            searchPagination: {
                filter: payload,
                search: ctx.getState().searchPagination.search,
                limit: 30,
                page: 1
            },
            productsListTitle: title
        });
    }

    @Action(GetBrands)
    getBrands(ctx: StateContext<ProductsModel>) {
        return this.service.getBrands().pipe(
            catchError((err) => of({ brands: [] })),
            tap((response) => {
                ctx.patchState({
                    brands: response.brands
                });
            })
        );
    }

    @Action(AddFavoriteBrand)
    addBrandToFav(ctx: StateContext<ProductsModel>, { payload }: AddFavoriteBrand) {
        ctx.setState(
            patch<ProductsModel>({
                favBrands: append<string>([payload])
            })
        );
    }

    @Action(RemoveFavoriteBrand)
    removeBrandToFav(ctx: StateContext<ProductsModel>, { payload }: RemoveFavoriteBrand) {
        ctx.setState(
            patch<ProductsModel>({
                favBrands: removeItem<string>((name) => name === payload)
            })
        );
    }

    @Action(BrandsSearch)
    searchBrandByName(ctx: StateContext<ProductsModel>, { payload }: BrandsSearch) {
        return this.service.getBrandsWithSearch(payload).pipe(
            tap((response) => {
                ctx.patchState({
                    brands: response.brands
                });
            })
        );
    }

    @Action(GetStandardFilters)
    getBaseFilters(ctx: StateContext<ProductsModel>) {
        return this.service.getStandardFilters().pipe(
            catchError((err) =>
                of({
                    filters: [],
                    selectedItems: []
                })
            ),
            tap((response) => {
                ctx.patchState({
                    searchFilters: response,
                    standardFilters: response
                });
            })
        );
    }
}
